import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { CloudFile } from "@/types/cloud";

interface CloudImportParams {
  provider: "google_drive" | "onedrive";
  files: CloudFile[];
  agentId?: string;
}

export const useCloudImportMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CloudImportParams) => {
      const { data, error } = await supabase.functions.invoke("cloud-storage", {
        body: {
          action: "import-files",
          data: {
            provider: params.provider,
            files: params.files,
            agentId: params.agentId && params.agentId !== "none" ? params.agentId : undefined,
          },
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to import files");
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to import files");
      }

      return data;
    },
    onSuccess: (data) => {
      toast.success(`Successfully imported ${data.documents?.length || 0} file(s)`);

      // Invalidate document queries
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Failed to import files";
      toast.error(message);
    },
  });
};
